// InsightsPanels — the value-added, chart-first analytics (A trend · B order-to-
// cash · C customers · D cash & risk) as an embeddable block. Four tabs, each
// lazy-loading its endpoint. Rendered as a section of the Report screen.

import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { InsightsApi } from "../hooks/useApi.js";
import { TrendLine, StackedArea, ThemedBars, Pareto, Heatmap, CohortGrid, ChartFrame } from "./InsightsCharts.jsx";
import { usePrinting } from "./PrintContext.jsx";
import { PALETTE } from "../lib/format.js";

const TABS = [
  ["trends", "Trend & momentum"],
  ["operations", "Order-to-cash"],
  ["customers", "Customers"],
  ["cashrisk", "Cash & risk"],
];

/* pivot long rows → wide objects keyed by `rowKey`, columns from distinct colKey. */
function pivot(rows, rowKey, colKey, valKey) {
  const out = {};
  for (const r of rows ?? []) {
    (out[r[rowKey]] ??= { [rowKey]: r[rowKey] })[r[colKey]] = r[valKey] ?? 0;
  }
  return Object.values(out);
}
const BACKLOG_ORDER = ["0-30 days", "31-60 days", "61-90 days", "90+ days"];
// min(420px,100%) lets a card shrink below 420px on narrow phones instead of
// forcing horizontal overflow off the right edge of the report.
const grid = { display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(min(420px,100%),1fr))", gap: 16 };

function TrendsTab({ d }) {
  const totals = {};
  (d.categoryMix ?? []).forEach((r) => { totals[r.category] = (totals[r.category] ?? 0) + (r.revenue ?? 0); });
  const top = Object.entries(totals).sort((a, b) => b[1] - a[1]).slice(0, 6).map(([c]) => c);
  const folded = (d.categoryMix ?? []).map((r) => ({ ...r, category: top.includes(r.category) ? r.category : "Other" }));
  const mixWide = pivot(folded, "month", "category", "revenue");
  const mixKeys = [...top, "Other"].map((c, i) => ({ key: c, name: c, color: PALETTE[i % PALETTE.length] }));

  return (
    <div className="ins-grid" style={grid}>
      <ChartFrame title="Monthly revenue + 3-month momentum" sub="Smooths the spiky months; rolling average shows direction of travel.">
        <TrendLine data={d.monthlyRevenue} lines={[{ key: "revenue", name: "Revenue", color: PALETTE[0] }, { key: "rolling3m", name: "3-mo avg", color: PALETTE[6] }]} />
      </ChartFrame>
      <div style={{ gridColumn: "1 / -1" }}><Heatmap cells={d.seasonality ?? []} /></div>
      <div style={{ gridColumn: "1 / -1" }}>
        <ChartFrame title="Category mix over time (share)" sub="How the revenue blend shifts month to month." height={320}>
          <StackedArea data={mixWide} keys={mixKeys} percent />
        </ChartFrame>
      </div>
    </div>
  );
}

function OperationsTab({ d }) {
  const backlog = [...(d.backlogAging ?? [])].sort((a, b) => BACKLOG_ORDER.indexOf(a.bucket) - BACKLOG_ORDER.indexOf(b.bucket));
  return (
    <div className="ins-grid" style={grid}>
      <ChartFrame title="Delivery-promise accuracy" sub="% of orders invoiced on/before their estimated date (orders carrying a promise date).">
        <TrendLine data={d.promiseAccuracy} lines={[{ key: "on_time_pct", name: "On-time %", color: PALETTE[3] }]} money={false} />
      </ChartFrame>
      <ChartFrame title="Open-order backlog by age" sub="Most of the R1.4bn backlog is 90+ days old — a stale-order signal.">
        <ThemedBars data={backlog} xKey="bucket" valueKey="open_value" />
      </ChartFrame>
      <ChartFrame title="Returns rate over time" sub="Returned units as % of delivered — a quality/dispute early-warning.">
        <TrendLine data={d.returnsTrend} lines={[{ key: "return_rate_pct", name: "Return rate %", color: PALETTE[6] }]} money={false} />
      </ChartFrame>
      <ChartFrame title="Credit notes over time" sub="Value credited back each month.">
        <TrendLine data={d.creditTrend} lines={[{ key: "credit_value", name: "Credits", color: PALETTE[6] }]} />
      </ChartFrame>
      <div style={{ gridColumn: "1 / -1" }}>
        <ChartFrame title="Blend production pipeline" sub="For recipe products: ordered → produced → delivered units over time." height={300}>
          <TrendLine data={d.blendPipeline}
            lines={[{ key: "ordered", name: "Ordered", color: PALETTE[8] }, { key: "produced", name: "Produced", color: PALETTE[2] }, { key: "delivered", name: "Delivered", color: PALETTE[3] }]}
            money={false} />
        </ChartFrame>
      </div>
    </div>
  );
}

function CustomersTab({ d }) {
  return (
    <div className="ins-grid" style={grid}>
      <div style={{ gridColumn: "1 / -1" }}><CohortGrid rows={d.cohorts ?? []} /></div>
      <ChartFrame title="Customer concentration (Lorenz curve)" sub="How much revenue the top customers carry. Dashed = perfect equality.">
        <Pareto data={d.concentration ?? []} />
      </ChartFrame>
      <ChartFrame title="Top-10 customer share by year" sub="Is reliance on the biggest accounts growing?">
        <ThemedBars data={d.top10ShareByYear} xKey="year" valueKey="top10_pct" money={false} />
      </ChartFrame>
      <div style={{ gridColumn: "1 / -1" }}>
        <ChartFrame title="New vs existing customer revenue" sub="How much each month's revenue comes from first-time vs returning customers.">
          <StackedArea data={d.newVsExisting}
            keys={[{ key: "existing_rev", name: "Existing", color: PALETTE[0] }, { key: "new_rev", name: "New", color: PALETTE[4] }]} />
        </ChartFrame>
      </div>
    </div>
  );
}

function CashRiskTab({ d }) {
  const statuses = [...new Set((d.collectionsByMonth ?? []).map((r) => r.status))];
  const collWide = pivot(d.collectionsByMonth, "month", "status", "value");
  const collKeys = statuses.map((s, i) => ({ key: s, name: s, color: PALETTE[i % PALETTE.length] }));
  return (
    <div className="ins-grid" style={grid}>
      <ChartFrame title="Collections — payment status mix" sub="Invoice value by payment status (confirm 'Posted'/'Closed' meanings with client).">
        <ThemedBars data={d.collectionsMix} xKey="status" valueKey="value" />
      </ChartFrame>
      <ChartFrame title="Gross-margin % over time" sub="On costed lines only (~52% of lines carry a cost).">
        <TrendLine data={d.marginTrend} lines={[{ key: "margin_pct", name: "Margin %", color: PALETTE[3] }]} money={false} />
      </ChartFrame>
      <div style={{ gridColumn: "1 / -1" }}>
        <ChartFrame title="Invoiced value by payment status over time" sub="Stacked monthly value by status.">
          <StackedArea data={collWide} keys={collKeys} />
        </ChartFrame>
      </div>
      <ChartFrame title="Commission clawbacks over time" sub="Reversed commission value each month — a margin-leakage signal.">
        <TrendLine data={d.clawbacks} lines={[{ key: "clawback_value", name: "Clawbacks", color: PALETTE[6] }]} />
      </ChartFrame>
    </div>
  );
}

const TAB_COMPONENT = { trends: TrendsTab, operations: OperationsTab, customers: CustomersTab, cashrisk: CashRiskTab };

const InsightsPanels = forwardRef(function InsightsPanels({ runId }, ref) {
  const [tab, setTab] = useState("trends");
  const [cache, setCache] = useState({});
  const [err, setErr] = useState(null);
  const printing = usePrinting();
  const keyFor = (t) => `${t}|${runId ?? ""}`;

  const ckey = keyFor(tab);   // refetch per tab AND per pinned run
  useEffect(() => {
    if (cache[ckey]) return;
    setErr(null);
    InsightsApi[tab](runId)
      .then((d) => setCache((c) => ({ ...c, [ckey]: d })))
      .catch((e) => setErr(e.body?.error ?? e.message));
  }, [ckey, cache]); // eslint-disable-line react-hooks/exhaustive-deps

  // In print mode, eagerly load EVERY tab so all four render stacked (not just the
  // active one). Re-runs as the cache fills, fetching each still-missing tab.
  useEffect(() => {
    if (!printing) return;
    for (const [k] of TABS) {
      const key = keyFor(k);
      if (cache[key]) continue;
      InsightsApi[k](runId).then((d) => setCache((c) => ({ ...c, [key]: d }))).catch(() => {});
    }
  }, [printing, cache, runId]); // eslint-disable-line react-hooks/exhaustive-deps

  // For PDF export: fetch every tab's data so all four can render stacked.
  useImperativeHandle(ref, () => ({
    ensureAllLoaded: async () => {
      const entries = await Promise.all(TABS.map(async ([k]) => {
        const key = keyFor(k);
        if (cache[key]) return [key, cache[key]];
        try { return [key, await InsightsApi[k](runId)]; } catch { return [key, null]; }
      }));
      setCache((c) => ({ ...c, ...Object.fromEntries(entries) }));
    },
  }), [runId, cache]); // eslint-disable-line react-hooks/exhaustive-deps

  // Print/PDF: render all tabs stacked (each labelled) instead of the single active tab.
  if (printing) {
    return (
      <div>
        {TABS.map(([k, label]) => {
          const dd = cache[keyFor(k)];
          const Tab = TAB_COMPONENT[k];
          // NOTE: no breakInside:avoid on the tab wrapper — a tab (3–5 charts) is far
          // taller than a page, so keeping it whole would punt the ENTIRE tab to the next
          // page and leave a big blank gap. Let it FLOW: each chart card stays atomic on
          // its own (.card{break-inside:avoid} in print CSS) and the label sticks to its
          // first chart via h3{break-after:avoid}.
          return (
            <div key={k} style={{ marginBottom: 24 }}>
              <h3 className="section-title" style={{ margin: "0 0 12px" }}>{label}</h3>
              {dd ? <Tab d={dd} /> : <div className="panel hint">No data.</div>}
            </div>
          );
        })}
      </div>
    );
  }

  const d = cache[ckey];
  const Active = TAB_COMPONENT[tab];

  return (
    <div>
      <div className="seg" style={{ display: "inline-flex", gap: 4, marginBottom: 16, flexWrap: "wrap" }}>
        {TABS.map(([k, label]) => (
          <button key={k} type="button" className={`btn ${tab === k ? "primary" : "ghost"} sm`} onClick={() => setTab(k)}>{label}</button>
        ))}
      </div>

      {err && <div className="panel" style={{ color: "var(--neg)" }}>Couldn’t load: {err}</div>}
      {!d && !err && <div className="panel hint">Loading…</div>}

      {d && <Active d={d} />}
    </div>
  );
});

export default InsightsPanels;
